import React from 'react'
import {Box, Card as MuiCard, CardContent, Divider, SxProps, Theme, Typography} from '@mui/material'
import {CARD_BORDER_COLOR, CARD_BORDER_RADIUS, NEUTRAL_SCALE} from 'src/theme-new/tokens'

interface CardProps {
  children: React.ReactNode
  title?: React.ReactNode
  action?: React.ReactNode
  /**
   * Optional bottom section, divided from `children` — e.g. row actions pinned to the
   * card's bottom-right on mobile instead of alongside `title`/`action` up top.
   */
  footer?: React.ReactNode
  /**
   * Set to skip the divider normally rendered between `title` and `children` — e.g. a
   * mobile card whose header is already a self-contained identity block (avatar + name)
   * and doesn't need a hard rule separating it from the fields below.
   */
  hideDivider?: boolean
  sx?: SxProps<Theme>
}

export default function Card({children, title, action, footer, hideDivider, sx}: CardProps) {
  const hasContent = children !== undefined && children !== null && children !== false

  return (
    <MuiCard
      variant="outlined"
      sx={{
        borderRadius: CARD_BORDER_RADIUS,
        borderColor: CARD_BORDER_COLOR,
        boxShadow: 'none',
        ...sx,
      }}
    >
      {title !== undefined && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              px: 2,
              pt: 2,
              pb: 1.5,
            }}
          >
            {/*
              Plain-string titles keep the original heading typography, with noWrap so a
              long, space-less title (e.g. a filename) ellipsizes instead of overflowing
              past the card and pushing `action` off-screen. Rich/JSX titles (e.g. a
              composite avatar+name mobile-card header) render as-is — the caller owns
              their own typography, since wrapping arbitrary block content in an <h3>
              would be invalid nesting. Always wrapped in a Box (even when empty) so
              `action` keeps its pinned-right position via justify-content: space-between.
            */}
            <Box sx={{minWidth: 0, flex: 1, overflow: 'hidden'}}>
              {typeof title === 'string' ? (
                <Typography
                  variant="h3"
                  noWrap
                  sx={{color: NEUTRAL_SCALE[900], fontWeight: 600, fontSize: 16}}
                >
                  {title}
                </Typography>
              ) : title}
            </Box>
            {action && <Box sx={{flexShrink: 0, display: 'flex'}}>{action}</Box>}
          </Box>
          {hasContent && !hideDivider && <Divider />}
        </>
      )}
      {hasContent && <CardContent sx={title !== undefined ? {pt: 2} : undefined}>{children}</CardContent>}
      {footer && (
        <>
          <Divider />
          <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 0.5, px: 2, py: 1}}>
            {footer}
          </Box>
        </>
      )}
    </MuiCard>
  )
}
